/* eslint-disable @typescript-eslint/no-explicit-any -- the Azure Maps SDK is loaded from the CDN without typings */
import { AZURE_MAPS_SUBSCRIPTION_KEY, loadAzureMapsSdk, type Location } from '@/components/azure-map/azureMapsSdk'
import DocumentHead from '@/components/document-head/DocumentHead'
import type { sitesMapPageRouteLoader } from '@/routes/map/SitesMapPage.route'
import SitesMapFiltersPanel from '@/routes/map/SitesMapPage.filters'
import { useDebounce, useSiteMapPoints } from '@/routes/map/SitesMapPage.hooks'
import { addSiteLayers, getPinIconOptions, toSiteFeature } from '@/routes/map/SitesMapPage.layers'
import SitesMapSitePanel from '@/routes/map/SitesMapPage.sitePanel'
import SitesMapSitePopup from '@/routes/map/SitesMapPage.sitePopup'
import SitesMapGeocodingIssues from '@/routes/map/SitesMapPage.geocodingIssues'
import { useAzureManager } from '@/auth/azure/azureManager'
import { Roles } from '@/auth/azure/Roles'
import type { MapBounds, MapSite, SiteMapFilters } from '@/routes/map/SitesMapPage.types'
import {
  DEFAULT_FILTERS,
  filterSites,
  getBoundingBox,
  getFilterOptions,
  getRadiusKm,
  hasActiveFilters,
  sortSites,
  toQueryBounds,
} from '@/routes/map/SitesMapPage.utils'
import { Drawer } from 'devextreme-react/drawer'
import { LoadPanel } from 'devextreme-react/load-panel'
import notify from 'devextreme/ui/notify'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLoaderData } from 'react-router-typesafe'
import './SitesMapPage.scss'

const ITALY_CENTER: [number, number] = [12.4964, 41.9028]
const USER_RADIUS_KM = 5

const SitesMapPage = () => {
  const { title } = useLoaderData<typeof sitesMapPageRouteLoader>()
  const isAdmin = useAzureManager(
    (state) =>
      (state.userInfo?.roles?.hasRole(Roles.GlobalAdministrator) ?? false) ||
      (state.userInfo?.roles?.hasRole(Roles.Administrator) ?? false) ||
      (state.userInfo?.roles?.hasRole(Roles.Supervisor) ?? false),
  )
  const [issuesOpen, setIssuesOpen] = useState(false)

  const [searchInput, setSearchInput] = useState('')
  const search = useDebounce(searchInput, 300).trim()
  const [filters, setFilters] = useState<SiteMapFilters>(DEFAULT_FILTERS)
  const [visibleBounds, setVisibleBounds] = useState<MapBounds | null>(null)
  const [selectedSite, setSelectedSite] = useState<MapSite | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [isPanelExpanded, setIsPanelExpanded] = useState(false)
  const [isMapReady, setIsMapReady] = useState(false)
  const [isLocationMode, setIsLocationMode] = useState(false)
  const [mapZoom, setMapZoom] = useState(5)
  const [userLocation, setUserLocation] = useState<Location | null>(null)

  const autoFitRef = useRef(true)
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<any>(null)
  const dataSourceRef = useRef<any>(null)
  const pinLayerRef = useRef<any>(null)
  const userMarkerRef = useRef<any>(null)

  // Search and filters always work on every geocoded site; without them the map shows the visible area.
  const filtering = hasActiveFilters(filters, search)
  const allSites = useSiteMapPoints()

  const sites = useMemo(() => {
    const source = filtering
      ? filterSites(allSites.data ?? [], filters, search)
      : (allSites.data ?? []).filter(
          (site) =>
            !visibleBounds ||
            (site.longitude >= visibleBounds[0] &&
              site.latitude >= visibleBounds[1] &&
              site.longitude <= visibleBounds[2] &&
              site.latitude <= visibleBounds[3]),
        )
    return sortSites(source, userLocation)
  }, [filtering, allSites.data, visibleBounds, filters, search, userLocation])

  const filterOptions = useMemo(() => getFilterOptions(allSites.data ?? [], filters), [allSites.data, filters])

  const selectSite = useCallback((site: MapSite | null) => {
    setSelectedSite(site)
    setIsPanelExpanded(false)
  }, [])

  const closeSitePopup = useCallback(() => selectSite(null), [selectSite])

  const selectSiteFromList = useCallback(
    (site: MapSite | null) => {
      selectSite(site)
      if (!site || !mapRef.current) return
      autoFitRef.current = false
      mapRef.current.setCamera({
        center: [site.longitude, site.latitude],
        zoom: Math.max(mapRef.current.getCamera().zoom, 15),
        type: 'ease',
        duration: 500,
      })
    },
    [selectSite],
  )

  const updateFilters = useCallback((changes: Partial<SiteMapFilters>) => {
    autoFitRef.current = true
    setFilters((current) => ({ ...current, ...changes }))
  }, [])

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS)
    setSearchInput('')
    setSelectedSite(null)
  }, [])

  const resetFilters = useCallback(() => {
    clearFilters()
    setIsLocationMode(false)
  }, [clearFilters])

  const moveToUser = useCallback(
    (location: Location) => {
      clearFilters()
      autoFitRef.current = false
      setIsLocationMode(true)
      mapRef.current?.setCamera({
        bounds: getBoundingBox(location, USER_RADIUS_KM),
        padding: 20,
        type: 'fly',
        duration: 1000,
      })
    },
    [clearFilters],
  )

  const handleRecenterUser = useCallback(() => {
    if (!navigator.geolocation) {
      notify('Geolocalizzazione non supportata dal browser.', 'error', 3000)
      return
    }
    // eslint-disable-next-line sonarjs/no-intrusive-permissions -- requested only after the user presses the button
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = { lat: position.coords.latitude, lon: position.coords.longitude }
        setUserLocation(location)
        moveToUser(location)
      },
      () => {
        if (userLocation) moveToUser(userLocation)
        else notify('Impossibile recuperare la posizione GPS.', 'error', 3000)
      },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 30_000 },
    )
  }, [moveToUser, userLocation])

  const renderFilters = useCallback(
    () => (
      <SitesMapFiltersPanel
        filters={filters}
        options={filterOptions}
        onChange={updateFilters}
        onReset={resetFilters}
        onClose={() => setDrawerOpen(false)}
      />
    ),
    [filters, filterOptions, updateFilters, resetFilters],
  )

  useEffect(() => {
    if (allSites.error) notify('Impossibile caricare i dati delle sedi.', 'error', 3000)
  }, [allSites.error])

  useEffect(() => {
    if (search) autoFitRef.current = true
  }, [search])

  useEffect(() => {
    if (!mapRef.current) return
    const timer = setTimeout(() => mapRef.current?.resize(), 300)
    return () => clearTimeout(timer)
  }, [drawerOpen])

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return
    let active = true

    loadAzureMapsSdk<any>()
      .then((atlas) => {
        if (!active || !mapContainerRef.current) return
        const map = new atlas.Map(mapContainerRef.current, {
          authOptions: {
            authType: atlas.AuthenticationType.subscriptionKey,
            subscriptionKey: AZURE_MAPS_SUBSCRIPTION_KEY,
          },
          center: ITALY_CENTER,
          zoom: 5,
          style: 'road',
          view: 'Auto',
        })
        mapRef.current = map

        map.events.add('ready', () => {
          const { dataSource, pinLayer, pinLabelLayer } = addSiteLayers(atlas, map, selectSite)
          dataSourceRef.current = dataSource
          pinLayerRef.current = pinLayer
          map.events.add('click', (event: any) => {
            // Layer clicks select sites; only clicks on the map background dismiss the detail.
            const target = event.originalEvent?.target
            if (target instanceof Element && target.closest('.sites-map-popup')) return
            const shapes = map.layers.getRenderedShapes(event.position, [pinLayer, pinLabelLayer])
            const clickedSite = shapes.some((shape: any) => (shape.getProperties?.() ?? shape.properties)?.site)
            if (!clickedSite) closeSitePopup()
          })
          map.events.add('moveend', () => {
            const camera = map.getCamera()
            setMapZoom(camera.zoom)
            setVisibleBounds(toQueryBounds(camera.bounds))
          })
          setIsMapReady(true)
        })
      })
      .catch((error) => {
        if (active) console.error('Errore durante il caricamento di Azure Maps SDK:', error)
      })

    return () => {
      active = false
      mapRef.current?.dispose()
      mapRef.current = null
    }
  }, [selectSite, closeSitePopup])

  useEffect(() => {
    const atlas = (window as any).atlas
    if (!isMapReady || !atlas) return
    dataSourceRef.current.clear()
    dataSourceRef.current.add(sites.map((site) => toSiteFeature(atlas, site)))
  }, [isMapReady, sites])

  // Fit the camera on the first load and after the user changes search or filters.
  useEffect(() => {
    const atlas = (window as any).atlas
    if (!isMapReady || !atlas || !autoFitRef.current || !allSites.data) return
    autoFitRef.current = false
    if (sites.length === 1) {
      mapRef.current.setCamera({
        center: [sites[0].longitude, sites[0].latitude],
        zoom: 14,
        type: 'ease',
        duration: 500,
      })
    } else if (sites.length > 1) {
      const positions = sites.map((site) => [site.longitude, site.latitude])
      mapRef.current.setCamera({
        bounds: atlas.data.BoundingBox.fromPositions(positions),
        padding: 60,
        type: 'ease',
        duration: 500,
      })
    }
  }, [isMapReady, sites, allSites.data])

  useEffect(() => {
    pinLayerRef.current?.setOptions({ iconOptions: getPinIconOptions(selectedSite?.siteId ?? null) })
  }, [selectedSite])

  useEffect(() => {
    const atlas = (window as any).atlas
    if (!isMapReady || !atlas || !userLocation) return
    const position = [userLocation.lon, userLocation.lat]
    if (userMarkerRef.current) {
      userMarkerRef.current.setOptions({ position })
      return
    }
    const markerElement = document.createElement('div')
    markerElement.className = 'sites-map__user-marker'
    userMarkerRef.current = new atlas.HtmlMarker({ htmlContent: markerElement, position, pixelOffset: [0, 0] })
    mapRef.current.markers.add(userMarkerRef.current)
  }, [isMapReady, userLocation])

  return (
    <>
      <DocumentHead title={title} />
      <div className={'content-block dx-card responsive-paddings'}>
        <div className={'sites-map'}>
          <LoadPanel visible={allSites.isLoading} message={'Caricamento sedi...'} />

          <div className={'sites-map__toolbar'}>
            {isAdmin && (
              <button
                type="button"
                className="sites-map__icon-button"
                onClick={() => setIssuesOpen(true)}
                title="Sedi da geocodificare"
                aria-label="Sedi da geocodificare"
              >
                ⚠
              </button>
            )}
            <button
              type={'button'}
              className={'sites-map__icon-button'}
              onClick={() => setDrawerOpen((open) => !open)}
              title={'Apri Filtri'}
            >
              ☰
            </button>
            <input
              type={'text'}
              className={'sites-map__search'}
              placeholder={'Cerca azienda o città...'}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <button type={'button'} className={'sites-map__icon-button'} onClick={() => setSearchInput('')}>
                ✕
              </button>
            )}
            <button
              type={'button'}
              className={'sites-map__locate-button'}
              onClick={handleRecenterUser}
              title={'Ricentra sulla mia posizione'}
            >
              🎯
            </button>
          </div>

          <Drawer
            opened={drawerOpen}
            render={renderFilters}
            openedStateMode={'shrink'}
            position={'left'}
            revealMode={'slide'}
            height={'100%'}
            closeOnOutsideClick
            onOptionChanged={(e) => {
              if (e.name === 'opened') setDrawerOpen(e.value)
            }}
          >
            <div ref={mapContainerRef} className={'sites-map__canvas'} />
          </Drawer>

          <SitesMapSitePanel
            sites={sites}
            expanded={isPanelExpanded}
            radiusKm={isLocationMode ? getRadiusKm(mapZoom) : null}
            onToggleExpanded={() => {
              closeSitePopup()
              setIsPanelExpanded((expanded) => !expanded)
            }}
            onSelectSite={selectSiteFromList}
          />
          {selectedSite && isMapReady && (
            <SitesMapSitePopup map={mapRef.current} site={selectedSite} onClose={closeSitePopup} />
          )}
        </div>
      </div>
      {isAdmin && <SitesMapGeocodingIssues opened={issuesOpen} onClose={() => setIssuesOpen(false)} />}
    </>
  )
}

export default SitesMapPage
